import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { Button, Grid, useMediaQuery, useTheme, Divider } from '@mui/material';
import Paper from '@mui/material/Paper';
import { CreateServiceImage, ZeroResultsImage } from '@/resources/images';
import {
    FilterButton,
    TableCard,
    TableMaker,
    TableMobileLabel,
    PageEmptyState,
    PageTitle,
    Pagination,
    SearchInput,
    SEO,
    SortButton,
    ListingClearButton,
    WrapWithPaper,
    TableSkeleton
} from '@/components/common';
import { ServiceFilterDrawer } from '@/components/FilterDrawers';
import { productServiceSortMenuItems } from '@/config';
import { SERVICE_BASE_PARAM } from '@/constants';
import {
    useCheckHasBusinessLoaded,
    useCurrency,
    useLocale,
    useListingState,
    useUrlParams
} from '@/hooks';
import { getPaginatedProducts } from '@/modules/dataWrangler/dataWrangler';
import { openDrawer, fetchListingItems } from '@/state/actions';
import { Container } from '@/containers';
import { ServicesActive } from '@/resources/icons';
import tableHead from './tableHead';
import { generateComponents, dataProcessor } from './dataProcessors';
import useStyles from '../styles';

const Services = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const { code: currency } = useCurrency();
    const locale = useLocale();
    const hasBusinessLoaded = useCheckHasBusinessLoaded();
    const { setUrlParams, getUrlParams } = useUrlParams();

    const {
        hasData,
        items,
        loadingItems,
        loadingPaper,
        totalPages,
        zeroResultsReturned
    } = useListingState('service');

    const classes = useStyles({ hasData });

    const dispatch = useDispatch();

    const currentUrlParams = getUrlParams({ base: SERVICE_BASE_PARAM });
    const stringifiedParams = JSON.stringify(currentUrlParams);

    const setOrder = (property) => {
        const sortOrder =
            currentUrlParams?.sort?.type === property &&
            currentUrlParams?.sort?.order === 'asc'
                ? 'desc'
                : 'asc';

        const newUrlParams = {
            ...currentUrlParams,
            sort: {
                type: property,
                order: sortOrder
            }
        };

        setUrlParams({ base: SERVICE_BASE_PARAM, params: newUrlParams });
    };

    useEffect(() => {
        if (hasBusinessLoaded) {
            getPaginatedProducts({
                args: JSON.parse(stringifiedParams),
                type: 'service'
            });
        }
    }, [hasBusinessLoaded, stringifiedParams]);

    return (
        <>
            <SEO title="All Services" />
            <Container>
                {!loadingItems && !hasData && (
                    <PageEmptyState
                        image={<CreateServiceImage />}
                        content="Create Your First Service"
                        buttonText="Create Service"
                        onClick={() => {
                            dispatch(openDrawer('service'));
                        }}
                    />
                )}
                {hasData && !loadingPaper && (
                    <>
                        {!isMobile && (
                            <Paper elevation={0} variant="outlined">
                                <Grid
                                    container
                                    direction="row"
                                    spacing={1}
                                    wrap="nowrap"
                                    alignItems="center"
                                >
                                    <Grid item md={2}>
                                        <PageTitle
                                            icon={<ServicesActive />}
                                            title="Services"
                                        />
                                    </Grid>
                                    <Grid item md={4}>
                                        <div className={classes.searchWrap}>
                                            <SearchInput
                                                base={SERVICE_BASE_PARAM}
                                                id="dropdownSearch"
                                                label="Search"
                                                ariaLabel="Search"
                                            />
                                        </div>
                                    </Grid>

                                    <Grid
                                        item
                                        md={2}
                                        className={classes.filterButtonWrap}
                                    >
                                        <Grid container alignItems="center">
                                            <Grid item md={8}>
                                                <FilterButton
                                                    base={SERVICE_BASE_PARAM}
                                                    type="service"
                                                    watch={[
                                                        'price',
                                                        'type',
                                                        'quantity',
                                                        'tagIds'
                                                    ]}
                                                />
                                            </Grid>
                                            <Grid item md={4}>
                                                <ListingClearButton
                                                    base={SERVICE_BASE_PARAM}
                                                    watch={[
                                                        's',
                                                        'price',
                                                        'type',
                                                        'quantity',
                                                        'tagIds'
                                                    ]}
                                                />
                                            </Grid>
                                        </Grid>
                                    </Grid>

                                    <Grid
                                        item
                                        container
                                        justifyContent="flex-end"
                                        md={4}
                                        className={classes.newButtonContainer}
                                    >
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            fullWidth
                                            onClick={() =>
                                                dispatch(openDrawer('service'))
                                            }
                                            className={classes.newButton}
                                        >
                                            New Service
                                        </Button>
                                    </Grid>
                                </Grid>

                                <Divider />

                                <TableMaker
                                    actionCols={[4, 5, 6]}
                                    ariaTableName="Services Table"
                                    handleRequestSort={setOrder}
                                    headCells={tableHead}
                                    rowData={
                                        hasData && !zeroResultsReturned
                                            ? generateComponents({
                                                  array: dataProcessor(items),
                                                  currency,
                                                  classes,
                                                  locale
                                              })
                                            : []
                                    }
                                    order={
                                        currentUrlParams?.sort?.order || 'asc'
                                    }
                                    orderBy={currentUrlParams?.sort?.type || ''}
                                />
                                {zeroResultsReturned && (
                                    <PageEmptyState
                                        image={<ZeroResultsImage />}
                                        content="The selected filters returned 0 results."
                                        buttonText="Reset filters"
                                        onClick={() => {
                                            dispatch(
                                                fetchListingItems({
                                                    type: 'service'
                                                })
                                            );
                                            setUrlParams({
                                                base: SERVICE_BASE_PARAM,
                                                params: null
                                            });
                                        }}
                                        hidePaper
                                    />
                                )}
                                <Pagination
                                    base={SERVICE_BASE_PARAM}
                                    totalPages={totalPages}
                                />
                            </Paper>
                        )}

                        {isMobile && (
                            <>
                                <Grid
                                    container
                                    direction="row"
                                    spacing={1}
                                    wrap="nowrap"
                                    alignItems="center"
                                >
                                    <Grid item xs={6}>
                                        <PageTitle
                                            icon={<ServicesActive />}
                                            title="Services"
                                        />
                                    </Grid>

                                    <Grid
                                        item
                                        container
                                        justifyContent="flex-end"
                                        xs={6}
                                        className={classes.newButtonContainer}
                                    >
                                        <Grid
                                            item
                                            xs={12}
                                            className={classes.newButton}
                                        >
                                            <Button
                                                variant="contained"
                                                color="primary"
                                                fullWidth
                                                onClick={() =>
                                                    dispatch(
                                                        openDrawer('service')
                                                    )
                                                }
                                            >
                                                New Service
                                            </Button>
                                        </Grid>
                                    </Grid>
                                </Grid>
                                <Grid container>
                                    <Grid item xs={12}>
                                        <div className={classes.searchWrap}>
                                            <SearchInput
                                                base={SERVICE_BASE_PARAM}
                                                id="dropdownSearch"
                                                label="Search"
                                                ariaLabel="Search"
                                            />
                                        </div>
                                    </Grid>
                                </Grid>
                                <Grid container justifyContent="center">
                                    <Grid
                                        item
                                        container
                                        spacing={1}
                                        xs={12}
                                        direction="row"
                                        wrap="nowrap"
                                        alignItems="center"
                                        justifyContent="center"
                                    >
                                        <Grid
                                            item
                                            container
                                            xs={6}
                                            className={classes.sortButtonWrap}
                                        >
                                            <SortButton
                                                base={SERVICE_BASE_PARAM}
                                                isLoading={loadingItems}
                                                sortMenuItems={
                                                    productServiceSortMenuItems
                                                }
                                            />
                                        </Grid>
                                        <Grid
                                            item
                                            xs={6}
                                            className={classes.filterButtonWrap}
                                        >
                                            <FilterButton
                                                base={SERVICE_BASE_PARAM}
                                                type="service"
                                                watch={[
                                                    'price',
                                                    'type',
                                                    'quantity',
                                                    'tagIds'
                                                ]}
                                            />
                                        </Grid>
                                    </Grid>
                                    <Grid item md={4}>
                                        <ListingClearButton
                                            base={SERVICE_BASE_PARAM}
                                            watch={[
                                                's',
                                                'price',
                                                'type',
                                                'quantity',
                                                'sort',
                                                'tagIds'
                                            ]}
                                            hide={
                                                zeroResultsReturned ||
                                                loadingItems
                                            }
                                        />
                                    </Grid>
                                </Grid>
                                {hasData &&
                                    !zeroResultsReturned &&
                                    generateComponents({
                                        array: dataProcessor(items),
                                        currency,
                                        classes,
                                        locale,
                                        isMobile
                                    }).map((item) => (
                                        <TableCard
                                            key={item.id}
                                            title={item.serviceName}
                                            onClick={item.primaryClick}
                                            left={
                                                <>
                                                    <TableMobileLabel label="Hours" />
                                                    <span>{item.hours}</span>
                                                </>
                                            }
                                            right={
                                                <>
                                                    <TableMobileLabel label="Service Rate" />
                                                    <span>{item.rate}</span>
                                                </>
                                            }
                                            actions={item.actions}
                                            tags={item.tags}
                                        />
                                    ))}
                                {zeroResultsReturned && (
                                    <PageEmptyState
                                        image={<ZeroResultsImage />}
                                        content="The selected filters returned 0 results."
                                        buttonText="Reset filters"
                                        onClick={() => {
                                            dispatch(
                                                fetchListingItems({
                                                    type: 'service'
                                                })
                                            );
                                            setUrlParams({
                                                base: SERVICE_BASE_PARAM,
                                                params: null
                                            });
                                        }}
                                    />
                                )}
                                {totalPages > 1 && (
                                    <Paper elevation={0} variant="outlined">
                                        <Pagination
                                            base={SERVICE_BASE_PARAM}
                                            totalPages={totalPages}
                                        />
                                    </Paper>
                                )}
                            </>
                        )}
                    </>
                )}
                {loadingPaper && (
                    <WrapWithPaper removeWrap={isMobile}>
                        <TableSkeleton isMobile={isMobile} />
                    </WrapWithPaper>
                )}
                {!loadingPaper && <ServiceFilterDrawer />}
            </Container>
        </>
    );
};

export default Services;
